import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import * as Dialog from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';
import './TimelineYogaHistory.css';

const TimelineYogaHistory = () => {
  const { t } = useTranslation();
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [hoveredMilestone, setHoveredMilestone] = useState(null);
  
  const timelineData = [
    {
      id: 1,
      year: t('yoga_timeline.milestone1.year', '2500 BCE'),
      title: t('yoga_timeline.milestone1.title', 'Pre-classical seals'),
      blurb: t('yoga_timeline.milestone1.blurb', 'Archaeological excavations in the Indus Valley revealed seals depicting figures in meditation postures. These artifacts provide the earliest evidence of practices related to what would later become yoga.')
    },
    {
      id: 2,
      year: t('yoga_timeline.milestone2.year', '400 CE'),
      title: t('yoga_timeline.milestone2.title', 'Yoga Sūtras'),
      blurb: t('yoga_timeline.milestone2.blurb', 'Patañjali compiled the Yoga Sūtras, systematizing yogic thought into an eight-limbed path. This seminal text shifted yoga from a primarily ritualistic practice to a codified philosophical system aimed at stilling the fluctuations of the mind.')
    },
    {
      id: 3,
      year: t('yoga_timeline.milestone3.year', '12th c.'),
      title: t('yoga_timeline.milestone3.title', 'Haṭha texts'),
      blurb: t('yoga_timeline.milestone3.blurb', 'The development of Haṭha Yoga was documented in texts like the Haṭha Yoga Pradīpikā. These works emphasized physical postures, breath control, and bodily purification as means to prepare for higher meditative states.')
    },
    {
      id: 4,
      year: t('yoga_timeline.milestone4.year', '1893'),
      title: t('yoga_timeline.milestone4.title', 'Vivekananda in Chicago'),
      blurb: t('yoga_timeline.milestone4.blurb', 'Swami Vivekananda introduced yoga philosophy to Western audiences at the Parliament of World Religions in Chicago. His presentations on the rational and universal aspects of Vedantic philosophy sparked significant interest in Eastern spiritual traditions across America.')
    },
    {
      id: 5,
      year: t('yoga_timeline.milestone5.year', '1948'),
      title: t('yoga_timeline.milestone5.title', 'Iyengar teaches'),
      blurb: t('yoga_timeline.milestone5.blurb', 'B.K.S. Iyengar began teaching his precise, alignment-focused approach to yoga, eventually publishing \'Light on Yoga\' in 1966. His systematic method, props, and therapeutic applications made yoga accessible to people of all ages and physical conditions.')
    },
    {
      id: 6,
      year: t('yoga_timeline.milestone6.year', '1990s'),
      title: t('yoga_timeline.milestone6.title', 'Vinyasa boom'),
      blurb: t('yoga_timeline.milestone6.blurb', 'Flowing vinyasa styles gained immense popularity throughout Western countries, becoming mainstream fitness practices. This period saw yoga transform into a global phenomenon with various hybrid forms emerging, from power yoga to hot yoga and beyond.')
    }
  ];

  return (
    <section className="timeline-section">
      <h3 className="timeline-heading">{t('yoga_timeline.heading', 'The Evolution of Yoga')}</h3>
      
      <div className="timeline-container">
        {timelineData.map((milestone) => (
          <Dialog.Root 
            key={milestone.id}
            open={selectedMilestone === milestone.id}
            onOpenChange={(open) => open ? setSelectedMilestone(milestone.id) : setSelectedMilestone(null)}
          >
            <Dialog.Trigger asChild>
              <motion.div 
                className="timeline-milestone"
                onMouseEnter={() => setHoveredMilestone(milestone.id)}
                onMouseLeave={() => setHoveredMilestone(null)}
                onFocus={() => setHoveredMilestone(milestone.id)}
                onBlur={() => setHoveredMilestone(null)}
                whileHover={{ y: -5 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                <motion.div 
                  className="timeline-circle"
                  animate={{ 
                    scale: hoveredMilestone === milestone.id ? 1.3 : 1,
                    boxShadow: hoveredMilestone === milestone.id 
                      ? '0 4px 12px rgba(160, 231, 229, 0.7)' 
                      : '0 2px 8px rgba(160, 231, 229, 0.5)'
                  }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <span className="sr-only">
                    {t('yoga_timeline.view_details', 'View details about {{year}} - {{title}}', { 
                      year: milestone.year, 
                      title: milestone.title 
                    })}
                  </span>
                </motion.div>
                <div className="timeline-content">
                  <motion.p 
                    className="timeline-year"
                    animate={{ 
                      color: hoveredMilestone === milestone.id ? '#3B82F6' : '#1E293B'
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    {milestone.year}
                  </motion.p>
                  <motion.p 
                    className="timeline-title"
                    animate={{ 
                      fontWeight: hoveredMilestone === milestone.id ? 600 : 400
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    {milestone.title}
                  </motion.p>
                </div>
              </motion.div>
            </Dialog.Trigger>
            
            <Dialog.Portal>
              <Dialog.Overlay className="dialog-overlay" />
              <Dialog.Content className="dialog-content">
                <div className="dialog-header">
                  <Dialog.Title className="dialog-title">
                    {milestone.year} · {milestone.title}
                  </Dialog.Title>
                  <Dialog.Close asChild>
                    <button className="dialog-close" aria-label={t('close', 'Close')}>
                      <Cross2Icon />
                    </button>
                  </Dialog.Close>
                </div>
                <Dialog.Description className="dialog-description">
                  {milestone.blurb}
                </Dialog.Description>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        ))}
      </div>
    </section>
  );
};

export default TimelineYogaHistory; 